import { z } from 'zod';

import { jobArchitecturePathSchema } from '~/shared/data-contract/job-architecture';
import { profileTaskClassificationValueTransportSchema } from '~/shared/data-contract/profile-task';

const opaqueIdSchema = z.string().trim().min(1);

export const STEP_CAPABILITIES = ['NOT_WRITABLE', 'NEEDS_CONTEXT', 'ROUTINE', 'FULLY_SPECIFIED'] as const;
export const stepCapabilitySchema = z.enum(STEP_CAPABILITIES);
export type StepCapability = z.infer<typeof stepCapabilitySchema>;

export const STEP_ERROR_COSTS = [
    'IRREVERSIBLE',
    'COSTLY',
    'NOTICEABLE',
    'MINOR',
    'MACHINE_CHECKED',
    'TRIVIAL',
] as const;
export const stepErrorCostSchema = z.enum(STEP_ERROR_COSTS);
export type StepErrorCost = z.infer<typeof stepErrorCostSchema>;

export const AUTOMATION_LAYERS = ['family', 'cluster', 'role', 'level'] as const;
export const automationLayerSchema = z.enum(AUTOMATION_LAYERS);
export type AutomationLayer = z.infer<typeof automationLayerSchema>;

export const AUTOMATION_RANKING_SORTS = ['exposure'] as const;
export const automationRankingSortSchema = z.enum(AUTOMATION_RANKING_SORTS);
export type AutomationRankingSort = z.infer<typeof automationRankingSortSchema>;

/** The constants the step scoring turns the model's per-step ratings into task scores with. */
export const automationStepScoringSchema = z.object({
    capabilityFactor: z.partialRecord(stepCapabilitySchema, z.number()),
    coordinationHumanShare: z.number(),
    errorCostRating: z.partialRecord(stepErrorCostSchema, z.number()),
    mandatedHumanRetention: z.number(),
    retentionAnchors: z.object({ rating: z.number(), retention: z.number() }).array(),
});

export type AutomationStepScoring = z.infer<typeof automationStepScoringSchema>;

/**
 * The method block every KPI response carries: the horizon, where the "high exposure" threshold sits, and the
 * step scoring constants. The client reads the threshold instead of hardcoding it.
 */
export const automationMethodSchema = z.object({
    exposureHigh: z.number(),
    horizon: z.string(),
    stepScoring: automationStepScoringSchema,
    version: z.string(),
});

export type AutomationMethod = z.infer<typeof automationMethodSchema>;

/**
 * One item at a layer, sized for a canvas with thousands of points. Every ratio is a share of the item's own
 * assessed effort; no duration travels. Ratios are absent when nothing is assessed.
 */
export const automationPointSchema = z.object({
    coverage: z.number().nullish(),
    exposure: z.number().nullish(),
    id: opaqueIdSchema,
    parentId: opaqueIdSchema.nullish(),
    taskCount: z.number().int().nonnegative(),
    title: z.string(),
});

export type AutomationPoint = z.infer<typeof automationPointSchema>;

export const automationPointsResponseSchema = z.object({
    layer: automationLayerSchema,
    method: automationMethodSchema,
    points: automationPointSchema.array(),
    truncatedAt: z.number().int().nullish(),
});

export type AutomationPointsResponse = z.infer<typeof automationPointsResponseSchema>;

export const automationPointMatchSchema = z.object({
    id: opaqueIdSchema,
    title: z.string(),
});

export const automationSearchResponseSchema = z.object({
    layer: automationLayerSchema,
    matches: automationPointMatchSchema.array(),
});

export type AutomationSearchResponse = z.infer<typeof automationSearchResponseSchema>;

export const automationAncestorTitlesSchema = z.partialRecord(automationLayerSchema, z.string());

/** A task of the item with its step-based score: the share of the task's own effort software takes over. */
export const automationDetailTaskSchema = z.object({
    classification: profileTaskClassificationValueTransportSchema.nullish(),
    effortShare: z.number().nullish(),
    methodVersion: z.string().nullish(),
    releasedShare: z.number().nullish(),
    taskId: opaqueIdSchema,
    taskScore: z.number().nullish(),
    taskScoreLower: z.number().nullish(),
    taskScoreUpper: z.number().nullish(),
    taskVersion: z.number().int().positive(),
    title: z.string(),
});

export type AutomationDetailTask = z.infer<typeof automationDetailTaskSchema>;

export const automationDetailResponseSchema = z.object({
    ancestorTitles: automationAncestorTitlesSchema,
    exposureLower: z.number().nullish(),
    exposureUpper: z.number().nullish(),
    item: automationPointSchema,
    method: automationMethodSchema,
    path: jobArchitecturePathSchema,
    remaining: z.number().nullish(),
    tasks: automationDetailTaskSchema.array(),
});

export type AutomationDetailResponse = z.infer<typeof automationDetailResponseSchema>;

export const automationRankingRowSchema = z.object({
    ancestorTitles: automationAncestorTitlesSchema,
    item: automationPointSchema,
    path: jobArchitecturePathSchema,
});

export type AutomationRankingRow = z.infer<typeof automationRankingRowSchema>;

export const automationRankingResponseSchema = z.object({
    layer: automationLayerSchema,
    method: automationMethodSchema,
    rows: automationRankingRowSchema.array(),
    sortBy: automationRankingSortSchema,
});

export type AutomationRankingResponse = z.infer<typeof automationRankingResponseSchema>;

/** Where a task's effort goes after automation: released to software, or retained by people. */
export const AUTOMATION_HANDOVER_DESTINATIONS = ['RELEASED', 'RETAINED'] as const;
export const automationHandoverDestinationSchema = z.enum(AUTOMATION_HANDOVER_DESTINATIONS);
export type AutomationHandoverDestination = z.infer<typeof automationHandoverDestinationSchema>;

export const automationHandoverFlowSchema = z.object({
    classification: profileTaskClassificationValueTransportSchema.nullish(),
    destination: automationHandoverDestinationSchema,
    share: z.number(),
});

export type AutomationHandoverFlow = z.infer<typeof automationHandoverFlowSchema>;

/** Flows partition to 1.00 at two decimals; zero flows are omitted, an unassessed scope has none. */
export const automationHandoverResponseSchema = z.object({
    assessedItemCount: z.number().int().nonnegative(),
    flows: automationHandoverFlowSchema.array(),
    itemCount: z.number().int().nonnegative(),
    layer: automationLayerSchema,
    method: automationMethodSchema,
});

export type AutomationHandoverResponse = z.infer<typeof automationHandoverResponseSchema>;
